import { Button } from "@/components/ui/button";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import ExerciseCompletion from "@/components/ui/exercise-completion";
import { Overlay } from "@/components/ui/overlay";
import VideoPlayer from "@/components/ui/video-player";
import { useLocale } from "@/contexts/IntlContext";
import { isAdultExerciseGroup } from "@/lib/data/contentCatalog";
import ButterflyHugVideoMp4 from "@/assets/videos/exercises/butterfly-hug/butterfly-hug.mp4";
import ButterflyHugVideoWebm from "@/assets/videos/exercises/butterfly-hug/butterfly-hug.webm";
import ButterflyHugAr from "/subs/butterfly-hug.ar.vtt";
import ButterflyHugEn from "/subs/butterfly-hug.en.vtt";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import ConfirmationModal from "@/components/ui/confirmation-modal";

export const Route = createFileRoute(
  "/(content)/adults/exercises/(shared)/butterfly-hug/",
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    referrer: search?.referrer as string | undefined,
    referrerGroup: search?.referrerGroup as string | undefined,
  }),
  head: () => ({
    meta: [
      {
        title: "Butterfly Hug Exercise - HAMD",
      },
    ],
  }),
  beforeLoad: () => ({
    title: "butterflyHug.title",
    navbar: {
      showTitle: true,
    },
  }),
});

function RouteComponent() {
  const { locale } = useLocale();
  const { referrer, referrerGroup } = Route.useSearch();
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const startExercise = () => setShowVideo(true);
  const closeVideo = () => {
    setShowVideo(false);
    setIsCompleted(false);
  };
  const [showExitModal, setShowExitModal] = useState(false);
  const handleExitRequest = () => setShowExitModal(true);
  const handleExitConfirm = () => {
    setShowExitModal(false);
    closeVideo();
  };

  const handleEnded = () => setIsCompleted(true);

  const handleFinish = () => {
    closeVideo();
    if (referrer) {
      const group = isAdultExerciseGroup(referrerGroup) ? referrerGroup : undefined;
      navigate({
        to: `/adults/exercises/${referrer}` as "/",
        search: group ? { mode: "exercises", group } : undefined,
      });
    }
  };

  const handleRepeat = () => setIsCompleted(false);

  return (
    <ExerciseLayout
      contentClassName="py-7.5 space-y-18"
      actions={
        <Button className="w-full" onClick={startExercise}>
          <FormattedMessage id="common.startExercise" />
        </Button>
      }
    >
      <p className="text-small text-dark">
        <FormattedMessage id="butterflyHug.intro" />
      </p>

      <Overlay
        show={showVideo}
        hideCloseButton
        onClose={closeVideo}
        className="max-w-4xl"
        fullHeight
      >
        {isCompleted ? (
          <div className="flex items-center justify-center h-full p-6">
            <ExerciseCompletion
              onFinish={handleFinish}
              onRepeat={handleRepeat}
              completionMessageId="common.complete"
              completionExerciseNameMessageId="butterflyHug.title"
              finishButtonMessageId="common.finishExercise"
              repeatButtonMessageId="common.repeatExercise"
            />
          </div>
        ) : (
          <div className="relative h-full">
            <VideoPlayer
              sources={[
                { src: ButterflyHugVideoWebm, type: "video/webm" },
                { src: ButterflyHugVideoMp4, type: "video/mp4" },
              ]}
              autoPlay
              className="w-full h-full"
              onBack={handleExitRequest}
              onEnded={handleEnded}
              closeBackButton
              subtitleTracks={[
                {
                  src: ButterflyHugAr,
                  srcLang: "ar",
                  label: "العربية",
                  default: locale === "ar",
                },
                {
                  src: ButterflyHugEn,
                  srcLang: "en",
                  label: "English",
                  default: locale === "en",
                },
              ]}
              activeSubtitleLanguage={locale}
              captionsDir={locale === "ar" ? "rtl" : "ltr"}
              defaultCaptionsVisible
              showCaptionsToggle
              subtitleTextColor="subtitle-text-dark"
            />
          </div>
        )}
        <ConfirmationModal
          isOpen={showExitModal}
          onClose={() => setShowExitModal(false)}
          onConfirm={handleExitConfirm}
          titleKey="exercise.exit.title"
          messageKey="exercise.exit.message"
          confirmButtonTextKey="exercise.exit.confirm"
        />
      </Overlay>
    </ExerciseLayout>
  );
}
